import { useEffect, useState } from "react";
import { Loader2, LogOut, Mail, MailX, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "elyncor_admin_key";

async function fetchEnquiries(key) {
  const res = await fetch(`${API}/api/enquiries`, { headers: { "X-Admin-Key": key } });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok) throw new Error("failed");
  return res.json();
}

function fmt(iso) {
  return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function KeyGate({ onUnlock }) {
  const [value, setValue] = useState("");
  const [checking, setChecking] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setChecking(true);
    try {
      const data = await fetchEnquiries(value.trim());
      sessionStorage.setItem(STORAGE_KEY, value.trim());
      onUnlock(value.trim(), data);
    } catch (err) {
      toast.error(err.message === "unauthorized" ? "That access key is not correct." : "Could not load enquiries.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-key-form">
      <p className="text-[11px] tracking-[0.22em] uppercase text-[#8C7A56]/80 mb-6">Private · ELYNCOR ELEMENTS</p>
      <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1B1915] tracking-tight mb-8">Enquiries</h1>
      <label htmlFor="admin-key" className="text-[11px] tracking-[0.22em] uppercase text-[#8C7A56]/75">Access key</label>
      <input
        id="admin-key"
        type="password"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        required
        autoFocus
        className="w-full bg-transparent border-b border-[#1B1915]/25 focus:border-[#1B1915] outline-none py-3.5 text-base text-[#1A201C] transition-colors duration-300"
        data-testid="admin-key-input"
      />
      <button
        type="submit"
        disabled={checking}
        className="mt-8 inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#1B1915] text-[#FDFBF7] text-[12px] tracking-[0.18em] uppercase hover:bg-[#2C2822] disabled:opacity-60 transition-colors duration-300"
        data-testid="admin-key-submit-button"
      >
        {checking && <Loader2 className="w-4 h-4 animate-spin" />}
        {checking ? "Checking…" : "Open"}
      </button>
    </form>
  );
}

function EnquiryCard({ e }) {
  return (
    <article className="border border-[#1B1915]/12 bg-[#F4F0E6]/50 rounded-sm p-6 sm:p-8" data-testid={`enquiry-card-${e.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <h2 className="font-serif-display text-xl sm:text-2xl text-[#1B1915] tracking-tight" data-testid="enquiry-card-name">{e.name}</h2>
        <time className="text-[12px] tracking-[0.1em] uppercase text-[#1A201C]/55" data-testid="enquiry-card-date">{fmt(e.created_at)}</time>
      </div>
      <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm text-[#1A201C]/75 mb-5">
        <a href={`mailto:${e.email}`} className="underline underline-offset-4 hover:text-[#1B1915]" data-testid="enquiry-card-email">{e.email}</a>
        {e.phone && <a href={`tel:${e.phone}`} className="hover:text-[#1B1915]" data-testid="enquiry-card-phone">{e.phone}</a>}
      </div>
      <p className="text-base text-[#1A201C]/85 leading-relaxed whitespace-pre-line" data-testid="enquiry-card-message">{e.message}</p>
      {typeof e.email_sent === "boolean" && (
        <p className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-[#1A201C]/50" data-testid="enquiry-card-email-status">
          {e.email_sent ? <Mail className="w-3.5 h-3.5" /> : <MailX className="w-3.5 h-3.5" />}
          {e.email_sent ? "Email notification sent" : "Saved here only (email not delivered)"}
        </p>
      )}
    </article>
  );
}

export default function AdminEnquiries() {
  const [key, setKey] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    setLoading(true);
    try {
      setItems(await fetchEnquiries(k));
    } catch (err) {
      if (err.message === "unauthorized") {
        sessionStorage.removeItem(STORAGE_KEY);
        setKey("");
      } else toast.error("Could not load enquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key && items === null) load(key);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setKey("");
    setItems(null);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1A201C]" data-testid="admin-enquiries-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        {!key ? (
          <KeyGate onUnlock={(k, data) => { setKey(k); setItems(data); }} />
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <div>
                <p className="text-[11px] tracking-[0.22em] uppercase text-[#8C7A56]/80 mb-4">Private · ELYNCOR ELEMENTS</p>
                <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1B1915] tracking-tight" data-testid="admin-enquiries-title">
                  Enquiries {items && <span className="text-[#1A201C]/40">· {items.length}</span>}
                </h1>
              </div>
              <div className="flex items-center gap-6 text-[12px] tracking-[0.18em] uppercase">
                <button onClick={() => load(key)} className="inline-flex items-center gap-2 hover:text-[#1B1915] text-[#1A201C]/65" data-testid="admin-refresh-button">
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
                <button onClick={logout} className="inline-flex items-center gap-2 hover:text-[#1B1915] text-[#1A201C]/65" data-testid="admin-logout-button">
                  <LogOut className="w-3.5 h-3.5" /> Lock
                </button>
              </div>
            </div>
            {items === null || (loading && items.length === 0) ? (
              <p className="text-[#1A201C]/55" data-testid="admin-loading">Loading…</p>
            ) : items.length === 0 ? (
              <p className="text-[#1A201C]/55" data-testid="admin-empty">No enquiries yet.</p>
            ) : (
              <div className="space-y-6" data-testid="admin-enquiries-list">
                {items.map((e) => <EnquiryCard key={e.id} e={e} />)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
